import { formatDate } from '../../../utils';

// AI SDK UIMessages are `{ id, role, metadata?, parts }` — a just-sent user message and a
// streaming assistant reply carry no timestamp at all. Only server-loaded history has one.
// The registry is keyed by `message.id` because that is what welds a resolved time to its own
// message: it survives re-renders, list re-orders and scrolling through a long thread.
const firstSeen = new Map<string, Date>();

/** Coerce a Date / ISO string / epoch number, rejecting anything that does not parse. */
function toDate(value: any): Date | null {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
}

export function resolveMessageTimestamp(message: any): Date | null {
  if (!message?.id) {
    return null;
  }

  const provided = toDate(message.createdAt);
  if (provided) {
    return provided;
  }

  const seen = firstSeen.get(message.id);
  if (seen) {
    return seen;
  }

  const now = new Date();
  firstSeen.set(message.id, now);
  return now;
}

export function formatMessageTimestamp(date: Date): string {
  return formatDate(date, 'datetime');
}

/** Stamp first-seen times onto messages before persisting them, so a reload keeps the original send time. */
export function withResolvedTimestamps(messages: any[]): any[] {
  return messages.map((message: any) => {
    if (toDate(message?.createdAt)) {
      return message;
    }

    const date = resolveMessageTimestamp(message);
    if (!date) {
      return message;
    }

    return { ...message, createdAt: date.toISOString() };
  });
}
